import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  HostListener,
} from '@angular/core';
import * as echarts from 'echarts';
import { map } from 'rxjs/operators';
import { MileDrivenData } from '../../../shared/interfaces/ride-data/mile-drive.interface';
import { RidesService } from '../../../shared/services/rides.service';

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
  imports: [],
})
export class LineChartComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() dataBound = new EventEmitter<boolean>();
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  private chartData: MileDrivenData | null = null;
  private myChart: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;

  constructor(private readonly _ridesService: RidesService) {}

  @HostListener('window:resize')
  onResize() {
    if (this.myChart) {
      // Force chart to resize when window resizes
      setTimeout(() => {
        this.myChart?.resize();
      }, 100);
    }
  }

  ngOnInit(): void {
    this.getMilesDriven();
  }

  ngAfterViewInit(): void {
    // Render chart after view is initialized with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (this.chartData) {
        this.renderChart(this.chartData);
      }
    }, 100);

    // Set up ResizeObserver to watch for container size changes
    if (this.chartContainer && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.myChart) {
          setTimeout(() => {
            this.myChart?.resize();
          }, 100);
        }
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.myChart) {
      this.myChart.dispose();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getMilesDriven() {
    this._ridesService
      .getMilesDriven()
      .pipe(map((res: MileDrivenData) => res))
      .subscribe({
        next: (response: MileDrivenData) => {
          this.chartData = response;
          if (this.chartContainer) {
            this.renderChart(response);
          }
          this.dataBound.emit(true);
        },
        error: (error) => {
          console.error(
            'Error in component, should have been handled by service:',
            error,
          );
          this.dataBound.emit(true);
        },
      });
  }

  private renderChart(response: MileDrivenData) {
    if (!this.chartContainer) {
      console.warn('Chart container not available yet');
      return;
    }

    let days: any = response.data.map((a) => a.dateOfService);
    let data: any = response.data.map((a) => Number(a.distance).toFixed(2));
    type EChartsOption = echarts.EChartsOption;

    let chartDom = this.chartContainer.nativeElement;
    if (!chartDom) {
      console.error('Chart DOM element not found');
      return;
    }

    // Dispose existing chart if it exists
    if (this.myChart) {
      this.myChart.dispose();
    }

    // Initialize chart without explicit dimensions - let it auto-detect container size
    this.myChart = echarts.init(chartDom, null, {
      renderer: 'canvas',
    });

    let option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        confine: true, // Keep tooltip within chart bounds
        className: 'echarts-tooltip',
        textStyle: {
          fontSize: 12,
          lineHeight: 16,
        },
        padding: [8, 12],
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#283B56',
          },
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: days,
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: data,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#417eb8',
          },
          itemStyle: {
            color: '#417eb8',
          },
        },
      ],
    };

    this.myChart.setOption(option);

    // Trigger immediate resize to ensure chart fits container
    this.myChart.resize();

    this.dataBound.emit(true);
  }
}
